import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, uploadFormData, openAuthedFile } from "@/api/client";
import i18n from "@/i18n";
import { enumLabel } from "@/i18n/enumLabels";
import ServiceBookingPaxGrid from "@/components/common/ServiceBookingPaxGrid";
import {
  SERVICE_KINDS,
  SERVICE_BOOKING_STATUSES,
  COST_SCOPES,
  SERVICE_KIND_LABELS,
  SERVICE_BOOKING_STATUS_LABELS,
  COST_SCOPE_LABELS,
  ROOM_TYPE_LABELS,
  type ServiceKind,
  type ServiceBookingStatus,
  type CostScope,
  type RoomType,
} from "@ckgo2/shared/types";
import { formatPrice, sortCzech } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import {
  Plane,
  Plus,
  Trash2,
  Loader2,
  Save,
  X,
  Mail,
  Send,
  Sparkles,
  Paperclip,
  FileText,
  Star,
} from "lucide-react";

interface ServiceBookingRow {
  id: string;
  orderId?: string | null;
  supplierId?: string | null;
  componentId?: string | null;
  kind: ServiceKind;
  status: ServiceBookingStatus;
  snapshotName: string;
  cost: number;
  currency: string;
  costScope: CostScope;
  roomType?: RoomType | null;
  paxCount: number;
  confirmDeadline?: string | null;
  paymentDeadline?: string | null;
  paxCountDeadline?: string | null;
  assignedTo?: string | null;
  supplierName?: string | null;
  notes?: string | null;
  customizationNote?: string | null;
  allotmentId?: string | null;
}

interface RoomTypeVariantLite {
  roomType: RoomType;
  cost: number;
  currency?: string | null;
}

interface ConfirmationDoc {
  id: string;
  title: string;
  status: string;
  createdAt: string;
}

interface ComponentLite {
  id: string;
  name: string;
  kind: ServiceKind;
  cost: number;
  currency: string;
  costScope: CostScope;
  supplierId?: string | null;
  roomTypes?: RoomTypeVariantLite[];
}
interface SupplierLite {
  id: string;
  name: string;
  type?: string | null;
  contactEmail?: string | null;
}

// Service kind → the supplier type that fulfils it. Names differ (flight↔airline,
// transfer↔transport), so this is an explicit map, not a 1:1 string match. `other` → no
// preferred type (show every supplier ungrouped).
const KIND_TO_SUPPLIER_TYPE: Record<ServiceKind, string | null> = {
  flight: "airline",
  hotel: "hotel",
  insurance: "insurance",
  activity: "activity",
  visa: "visa",
  transfer: "transport",
  other: null,
};
const SUPPLIER_GROUP_LABEL: Record<string, string> = {
  airline: i18n.t("shared.serviceBookings.supplierGroups.airline"),
  hotel: i18n.t("shared.serviceBookings.supplierGroups.hotel"),
  insurance: i18n.t("shared.serviceBookings.supplierGroups.insurance"),
  transport: i18n.t("shared.serviceBookings.supplierGroups.transport"),
  guide: i18n.t("shared.serviceBookings.supplierGroups.guide"),
  activity: i18n.t("shared.serviceBookings.supplierGroups.activity"),
  visa: i18n.t("shared.serviceBookings.supplierGroups.visa"),
  other: i18n.t("shared.serviceBookings.supplierGroups.other"),
};
interface AllotmentLite {
  id: string;
  airline: string;
  label: string;
  status: string;
  used: number;
  seatsTotal: number;
  focReleaseDeadline?: string | null;
}

interface FormState {
  componentId: string;
  supplierId: string;
  kind: ServiceKind;
  status: ServiceBookingStatus;
  snapshotName: string;
  cost: number;
  currency: string;
  costScope: CostScope;
  roomType: string;
  paxCount: number;
  confirmDeadline: string;
  paxCountDeadline: string;
  paymentDeadline: string;
  notes: string;
  customizationNote: string;
  allotmentId: string;
}

const blankForm = (currency: string, pax: number, shared: boolean): FormState => ({
  componentId: "",
  supplierId: "",
  kind: "hotel",
  status: "draft",
  snapshotName: "",
  cost: 0,
  currency,
  // Shared (departure-level) services default to a per-departure cost (e.g. a chartered bus).
  costScope: shared ? "per_departure" : "per_pax",
  roomType: "",
  paxCount: pax || 1,
  confirmDeadline: "",
  paxCountDeadline: "",
  paymentDeadline: "",
  notes: "",
  customizationNote: "",
  allotmentId: "",
});

/**
 * Service bookings for an order OR a whole departure (Block 2 B2.1 + B2.3). Pass `orderId`
 * for an order's services, or `tripDateId` for a departure's SHARED services (vision §5.5 —
 * e.g. a bus everyone rides). The list/create/margin-invalidation all key off whichever
 * scope is supplied.
 */
export default function ServiceBookingsEditor({
  orderId = null,
  tripDateId = null,
  currency = "CZK",
  pax = 1,
}: {
  orderId?: string | null;
  tripDateId?: string | null;
  currency?: string;
  pax?: number;
}) {
  const { t } = useTranslation();
  const shared = !orderId && !!tripDateId;
  const scopeId = orderId ?? tripDateId ?? "";
  const scopeParam = orderId ? `orderId=${orderId}` : `tripDateId=${tripDateId}`;
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ to: string; subject: string; body: string } | null>(null);
  const [parseText, setParseText] = useState("");
  const [parsed, setParsed] = useState<Record<string, string> | null>(null);

  const { data } = useQuery({
    queryKey: ["service-bookings", scopeId],
    queryFn: () =>
      api.get<{ data: ServiceBookingRow[] }>(`/api/admin/service-bookings?${scopeParam}`),
  });
  const { data: compData } = useQuery({
    queryKey: ["supplier-components", "lookup"],
    queryFn: () => api.get<{ data: ComponentLite[] }>("/api/admin/supplier-components?limit=100"),
  });
  const { data: supData } = useQuery({
    queryKey: ["suppliers", "lookup"],
    queryFn: () => api.get<{ data: SupplierLite[] }>("/api/admin/suppliers?limit=100"),
  });
  // Active flight allotments for the drawdown picker (Block 4.10) — only loaded once, cheap.
  const { data: allotData } = useQuery({
    queryKey: ["flight-allotments", "lookup"],
    queryFn: () => api.get<{ data: AllotmentLite[] }>("/api/admin/flight-allotments"),
  });

  const bookings = data?.data ?? [];
  const components = compData?.data ?? [];
  // A–Z (`sortCzech`; the API returns newest first) — both relevance groups below inherit it.
  const suppliers = sortCzech(supData?.data ?? [], (s) => s.name);
  const allotments = (allotData?.data ?? []).filter((a) => a.status === "active");

  // Supplier dropdown is grouped by relevance to the chosen Kind (e.g. Hotel → hotels first), but
  // never hides the rest — a supplier may serve several kinds and the type field can be unset.
  const matchType = form ? KIND_TO_SUPPLIER_TYPE[form.kind] : null;
  const matchingSuppliers = matchType ? suppliers.filter((s) => s.type === matchType) : [];
  const otherSuppliers = matchType ? suppliers.filter((s) => s.type !== matchType) : suppliers;
  // The supplier email draft needs a linked supplier that has an email on file.
  const selectedSupplier = form?.supplierId
    ? (suppliers.find((s) => s.id === form.supplierId) ?? null)
    : null;
  const supplierEmailReady = !!selectedSupplier?.contactEmail;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["service-bookings", scopeId] });
    queryClient.invalidateQueries({
      queryKey: [orderId ? "order-margin" : "departure-margin", scopeId],
    });
  };

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editId
        ? api.put(`/api/admin/service-bookings/${editId}`, payload)
        : api.post("/api/admin/service-bookings", {
            ...payload,
            ...(orderId ? { orderId } : { tripDateId }),
          }),
    onSuccess: () => {
      invalidate();
      setForm(null);
      setEditId(null);
    },
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.put(`/api/admin/service-bookings/${id}`, { status }),
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/service-bookings/${id}`),
    onSuccess: invalidate,
  });
  const genDraft = useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: { to: string; subject: string; body: string } }>(
        `/api/admin/service-bookings/${id}/draft-supplier-email`,
        {},
      ),
    onSuccess: (r) => setDraft(r.data),
  });
  const sendDraft = useMutation({
    mutationFn: (id: string) =>
      api.post(`/api/admin/service-bookings/${id}/draft-supplier-email?send=1`, {}),
  });
  const parseDoc = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      api.post<{ data: Record<string, string> }>(
        `/api/admin/service-bookings/${id}/parse-document`,
        { text },
      ),
    onSuccess: (r) => setParsed(r.data),
  });

  // Supplier confirmation PDFs (Block 4.5) — attached to this service, flow to the guide PWA offline.
  const { data: confData } = useQuery({
    queryKey: ["service-confirmations", editId],
    queryFn: () =>
      api.get<{ data: ConfirmationDoc[] }>(`/api/admin/service-bookings/${editId}/confirmations`),
    enabled: !!editId,
  });
  const confirmations = confData?.data ?? [];
  const invalidateConf = () =>
    queryClient.invalidateQueries({ queryKey: ["service-confirmations", editId] });
  const uploadConf = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return uploadFormData(`/api/admin/service-bookings/${editId}/confirmation`, fd);
    },
    onSuccess: invalidateConf,
  });
  const delConf = useMutation({
    mutationFn: (docId: string) => api.delete(`/api/admin/service-bookings/confirmation/${docId}`),
    onSuccess: invalidateConf,
  });
  // "✨ AI fill" (Block 4.6) — read the PDF, prefill the extracted fields + suggest "confirmed".
  // It does NOT mutate the booking; the human reviews and Saves. The same file is attached in parallel.
  const aiFill = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return uploadFormData<{
        data: {
          extracted: Record<string, unknown>;
          suggestedStatus: ServiceBookingStatus;
          readable: boolean;
        };
      }>(`/api/admin/service-bookings/${editId}/confirmation/ai-extract`, fd);
    },
    onSuccess: (r) => {
      setParsed(
        Object.fromEntries(
          Object.entries(r.data.extracted)
            .filter(([, v]) => v != null && v !== "")
            .map(([k, v]) => [k, String(v)]),
        ),
      );
      if (r.data.readable) setForm((f) => (f ? { ...f, status: r.data.suggestedStatus } : f));
    },
  });

  const resetAux = () => {
    setDraft(null);
    setParsed(null);
    setParseText("");
  };
  const openNew = () => {
    setEditId(null);
    resetAux();
    setForm(blankForm(currency, pax, shared));
  };
  const openEdit = (b: ServiceBookingRow) => {
    setEditId(b.id);
    resetAux();
    setForm({
      componentId: b.componentId ?? "",
      supplierId: b.supplierId ?? "",
      kind: b.kind,
      status: b.status,
      snapshotName: b.snapshotName,
      cost: b.cost,
      currency: b.currency,
      costScope: b.costScope,
      roomType: b.roomType ?? "",
      paxCount: b.paxCount,
      confirmDeadline: b.confirmDeadline ?? "",
      paxCountDeadline: b.paxCountDeadline ?? "",
      paymentDeadline: b.paymentDeadline ?? "",
      notes: b.notes ?? "",
      customizationNote: b.customizationNote ?? "",
      allotmentId: b.allotmentId ?? "",
    });
  };

  // Selecting a catalog component prefills name/cost/kind/scope/supplier and clears any room-type
  // choice (it belongs to the previous component).
  const pickComponent = (id: string) => {
    const c = components.find((x) => x.id === id);
    setForm((f) =>
      f
        ? {
            ...f,
            componentId: id,
            roomType: "",
            ...(c
              ? {
                  snapshotName: c.name,
                  cost: c.cost,
                  currency: c.currency,
                  kind: c.kind,
                  costScope: c.costScope,
                  supplierId: c.supplierId ?? f.supplierId,
                }
              : {}),
          }
        : f,
    );
  };

  // Picking a room type (Block 6.1 Řez 2b) snapshots that variant's per-room cost into the form so
  // the operator sees the price that will be saved; the server re-derives it on save too.
  const pickRoomType = (roomType: string) => {
    setForm((f) => {
      if (!f) return f;
      const comp = components.find((x) => x.id === f.componentId);
      // Back to "— flat cost —": restore the component's flat cost so a previously-picked variant
      // cost doesn't silently linger and mis-price the booking.
      if (!roomType) {
        return {
          ...f,
          roomType: "",
          ...(comp ? { cost: comp.cost, currency: comp.currency } : {}),
        };
      }
      const variant = comp?.roomTypes?.find((v) => v.roomType === roomType);
      return {
        ...f,
        roomType,
        ...(variant ? { cost: variant.cost, currency: variant.currency || f.currency } : {}),
      };
    });
  };

  // Room-type variants of the currently-selected component (only shown for hotel bookings).
  const selectedComponent = form ? components.find((x) => x.id === form.componentId) : undefined;
  const roomTypeOptions = selectedComponent?.roomTypes ?? [];

  const submit = () => {
    if (!form) return;
    save.mutate({
      componentId: form.componentId || null,
      supplierId: form.supplierId || null,
      kind: form.kind,
      status: form.status,
      snapshotName: form.snapshotName,
      cost: Number(form.cost) || 0,
      currency: form.currency,
      costScope: form.costScope,
      // Room-type choice only applies to accommodation (Block 6.1 Řez 2b); cleared for other kinds.
      roomType: form.kind === "hotel" ? form.roomType || null : null,
      paxCount: Number(form.paxCount) || 1,
      confirmDeadline: form.confirmDeadline || null,
      paxCountDeadline: form.paxCountDeadline || null,
      paymentDeadline: form.paymentDeadline || null,
      notes: form.notes || null,
      customizationNote: form.customizationNote.trim() || null,
      // Drawdown link only applies to flights (Block 4.10); cleared for any other kind.
      allotmentId: form.kind === "flight" ? form.allotmentId || null : null,
    });
  };

  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <Plane className="h-5 w-5" /> {t("shared.serviceBookings.title")}
          <span className="text-sm font-normal text-gray-400">({bookings.length})</span>
        </h2>
        {!form && (
          <button onClick={openNew} className="btn-primary btn-sm">
            <Plus className="h-4 w-4" />
            {t("shared.serviceBookings.addService")}
          </button>
        )}
      </div>

      {bookings.length > 0 && (
        <div className="mb-4 space-y-2">
          {bookings.map((b) => (
            <div key={b.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => openEdit(b)}
                  className="flex-1 text-left"
                  title={t("shared.serviceBookings.editService")}
                >
                  <p className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-white">
                    {b.snapshotName}
                    {b.customizationNote && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800"
                        title={b.customizationNote}
                      >
                        <Star className="h-2.5 w-2.5" /> {t("shared.serviceBookings.deviation")}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {enumLabel(SERVICE_KIND_LABELS, b.kind)}
                    {b.supplierName ? ` · ${b.supplierName}` : ""}
                    {b.paymentDeadline
                      ? ` · ${t("shared.serviceBookings.payBy", { date: formatDate(b.paymentDeadline) })}`
                      : ""}
                  </p>
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatPrice(b.cost, b.currency)}
                  {b.costScope === "per_pax" && (
                    <span className="text-gray-400">
                      {t("shared.serviceBookings.perPaxSuffix")}
                    </span>
                  )}
                </span>
                <select
                  value={b.status}
                  onChange={(e) => setStatus.mutate({ id: b.id, status: e.target.value })}
                  className="input h-8 w-32 py-0 text-sm"
                >
                  {SERVICE_BOOKING_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {enumLabel(SERVICE_BOOKING_STATUS_LABELS, s)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => del.mutate(b.id)}
                  className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700"
                  title={t("common.actions.delete")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {/* Per-pax fulfilment grid (Block 5.7 Řez 1) — per-pax services only. */}
              {b.costScope === "per_pax" && (
                <div className="mt-2">
                  <ServiceBookingPaxGrid bookingId={b.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {editId
                ? t("shared.serviceBookings.editService")
                : t("shared.serviceBookings.newService")}
            </p>
            <button
              title={t("common.actions.close")}
              onClick={() => {
                setForm(null);
                setEditId(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label text-xs">{t("shared.serviceBookings.fromCatalog")}</label>
              <select
                value={form.componentId}
                onChange={(e) => pickComponent(e.target.value)}
                className="input"
              >
                <option value="">{t("shared.serviceBookings.manualEntry")}</option>
                {components.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({formatPrice(c.cost, c.currency)})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-xs">{t("common.fields.name")}</label>
              <input
                className="input"
                value={form.snapshotName}
                onChange={(e) => setForm({ ...form, snapshotName: e.target.value })}
                placeholder={t("shared.serviceBookings.namePlaceholder")}
              />
            </div>
            <div>
              <label className="label text-xs">{t("shared.serviceBookings.kind")}</label>
              <select
                className="input"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as ServiceKind })}
              >
                {SERVICE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {enumLabel(SERVICE_KIND_LABELS, k)}
                  </option>
                ))}
              </select>
            </div>
            {form.kind === "hotel" && roomTypeOptions.length > 0 && (
              <div>
                <label className="label text-xs">{t("shared.serviceBookings.roomType")}</label>
                <select
                  className="input"
                  value={form.roomType}
                  onChange={(e) => pickRoomType(e.target.value)}
                >
                  <option value="">{t("shared.serviceBookings.flatCost")}</option>
                  {roomTypeOptions.map((v) => (
                    <option key={v.roomType} value={v.roomType}>
                      {enumLabel(ROOM_TYPE_LABELS, v.roomType)} (
                      {formatPrice(v.cost, v.currency || form.currency)}
                      {t("shared.serviceBookings.perRoomSuffix")})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {form.kind === "flight" && (
              <div className="sm:col-span-2">
                <label className="label text-xs">
                  {t("shared.serviceBookings.allotmentLabel", { count: form.paxCount || 1 })}
                </label>
                <select
                  className="input"
                  value={form.allotmentId}
                  onChange={(e) => setForm({ ...form, allotmentId: e.target.value })}
                >
                  <option value="">{t("shared.serviceBookings.noBlock")}</option>
                  {allotments.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.airline} {a.label} ({a.used}/{a.seatsTotal}
                      {a.focReleaseDeadline ? `, FOC ${a.focReleaseDeadline}` : ""})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="label text-xs">{t("shared.serviceBookings.supplier")}</label>
              <select
                className="input"
                value={form.supplierId}
                onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
              >
                <option value="">{t("shared.serviceBookings.none")}</option>
                {matchingSuppliers.length > 0 && (
                  <optgroup
                    label={
                      (matchType && SUPPLIER_GROUP_LABEL[matchType]) ||
                      t("shared.serviceBookings.matching")
                    }
                  >
                    {matchingSuppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {otherSuppliers.length > 0 &&
                  (matchingSuppliers.length > 0 ? (
                    <optgroup label={t("shared.serviceBookings.otherSuppliers")}>
                      {otherSuppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </optgroup>
                  ) : (
                    otherSuppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))
                  ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:col-span-2">
              <div>
                <label className="label text-xs">{t("shared.serviceBookings.cost")}</label>
                <input
                  type="number"
                  className="input"
                  value={form.cost}
                  onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className="label text-xs">{t("shared.serviceBookings.scope")}</label>
                <select
                  className="input"
                  value={form.costScope}
                  onChange={(e) => setForm({ ...form, costScope: e.target.value as CostScope })}
                >
                  {COST_SCOPES.map((cs) => (
                    <option key={cs} value={cs}>
                      {enumLabel(COST_SCOPE_LABELS, cs)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label text-xs">{t("shared.serviceBookings.pax")}</label>
                <input
                  type="number"
                  className="input"
                  value={form.paxCount}
                  onChange={(e) => setForm({ ...form, paxCount: Number(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:col-span-2">
              <div>
                <label className="label text-xs">{t("shared.serviceBookings.confirmBy")}</label>
                <input
                  type="date"
                  className="input"
                  value={form.confirmDeadline}
                  onChange={(e) => setForm({ ...form, confirmDeadline: e.target.value })}
                />
              </div>
              <div>
                <label className="label text-xs">{t("shared.serviceBookings.paxCountBy")}</label>
                <input
                  type="date"
                  className="input"
                  value={form.paxCountDeadline}
                  onChange={(e) => setForm({ ...form, paxCountDeadline: e.target.value })}
                />
              </div>
              <div>
                <label className="label text-xs">{t("shared.serviceBookings.payByLabel")}</label>
                <input
                  type="date"
                  className="input"
                  value={form.paymentDeadline}
                  onChange={(e) => setForm({ ...form, paymentDeadline: e.target.value })}
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="label text-xs">{t("shared.serviceBookings.notesLabel")}</label>
              <textarea
                className="input"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={t("shared.serviceBookings.notesPlaceholder")}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label flex items-center gap-1 text-xs">
                <Star className="h-3 w-3 text-amber-500" />{" "}
                {t("shared.serviceBookings.customizationLabel")}
              </label>
              <input
                className="input"
                value={form.customizationNote}
                onChange={(e) => setForm({ ...form, customizationNote: e.target.value })}
                placeholder={t("shared.serviceBookings.customizationPlaceholder")}
              />
            </div>
          </div>

          <button onClick={submit} disabled={save.isPending} className="btn-primary btn-sm">
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {editId
              ? t("shared.serviceBookings.saveService")
              : t("shared.serviceBookings.addService")}
          </button>

          {editId && (
            <div className="space-y-4 border-t border-gray-200 pt-4 dark:border-gray-700">
              {/* Supplier email draft */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                    <Mail className="h-4 w-4" /> {t("shared.serviceBookings.supplierEmail")}
                  </p>
                  <button
                    onClick={() => genDraft.mutate(editId)}
                    disabled={genDraft.isPending || !supplierEmailReady}
                    className="btn-ghost btn-sm"
                    title={
                      supplierEmailReady ? undefined : t("shared.serviceBookings.linkSupplierFirst")
                    }
                  >
                    {genDraft.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    {t("shared.serviceBookings.generateDraft")}
                  </button>
                </div>
                {!supplierEmailReady && (
                  <p className="text-xs text-gray-500">
                    {form.supplierId
                      ? t("shared.serviceBookings.noEmailOnFile", {
                          name: selectedSupplier?.name ?? t("shared.serviceBookings.thisSupplier"),
                        })
                      : t("shared.serviceBookings.linkSupplierHint")}
                  </p>
                )}
                {draft && (
                  <div className="space-y-2 rounded-lg bg-white p-3 dark:bg-gray-900/40">
                    <input
                      className="input text-sm"
                      readOnly
                      value={t("shared.serviceBookings.emailTo", { to: draft.to || "—" })}
                    />
                    <input className="input text-sm" readOnly value={draft.subject} />
                    <textarea className="input text-sm" rows={8} readOnly value={draft.body} />
                    <button
                      onClick={() => sendDraft.mutate(editId)}
                      disabled={sendDraft.isPending || !draft.to}
                      className="btn-primary btn-sm"
                    >
                      {sendDraft.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {sendDraft.isSuccess
                        ? t("shared.serviceBookings.sent")
                        : t("shared.serviceBookings.sendViaGmail")}
                    </button>
                  </div>
                )}
              </div>

              {/* Supplier confirmation PDFs (Block 4.5) — shown to the guide offline. */}
              <div className="space-y-2">
                <p className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <Paperclip className="h-4 w-4" /> {t("shared.serviceBookings.confirmations")}
                </p>
                {confirmations.length > 0 && (
                  <ul className="space-y-1">
                    {confirmations.map((d) => (
                      <li
                        key={d.id}
                        className="flex items-center justify-between rounded bg-white px-2 py-1 text-sm dark:bg-gray-900/40"
                      >
                        <button
                          onClick={() => openAuthedFile(`/api/admin/documents/${d.id}/pdf`)}
                          className="text-brand flex items-center gap-1.5 hover:underline"
                        >
                          <FileText className="h-3.5 w-3.5" /> {d.title}
                        </button>
                        <button
                          onClick={() => delConf.mutate(d.id)}
                          className="rounded p-1 text-gray-400 hover:text-red-600"
                          title={t("common.actions.remove")}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2">
                  <label className="btn-ghost btn-sm inline-flex cursor-pointer">
                    {uploadConf.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Paperclip className="h-4 w-4" />
                    )}
                    {t("shared.serviceBookings.attachPdf")}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadConf.mutate(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <label className="btn-ghost btn-sm inline-flex cursor-pointer">
                    {aiFill.isPending || uploadConf.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {t("shared.serviceBookings.aiFill")}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          uploadConf.mutate(f); // attach the PDF…
                          aiFill.mutate(f); // …and prefill extracted fields + suggest "confirmed"
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-400">
                  {t("shared.serviceBookings.confirmationsHint")}{" "}
                  <strong>{t("shared.serviceBookings.aiFill")}</strong>{" "}
                  {t("shared.serviceBookings.aiFillHint")}
                </p>
              </div>

              {/* AI document parse */}
              <div className="space-y-2">
                <p className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                  <Sparkles className="h-4 w-4" /> {t("shared.serviceBookings.parseTitle")}
                </p>
                <textarea
                  className="input text-sm"
                  rows={4}
                  placeholder={t("shared.serviceBookings.parsePlaceholder")}
                  value={parseText}
                  onChange={(e) => setParseText(e.target.value)}
                />
                <button
                  onClick={() => parseDoc.mutate({ id: editId, text: parseText })}
                  disabled={parseDoc.isPending || !parseText.trim()}
                  className="btn-ghost btn-sm"
                >
                  {parseDoc.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {t("shared.serviceBookings.extractDetails")}
                </button>
                {parsed && (
                  <div className="rounded-lg bg-white p-3 text-sm dark:bg-gray-900/40">
                    {Object.entries(parsed).filter(([, v]) => v).length === 0 ? (
                      <p className="text-gray-400">
                        {t("shared.serviceBookings.nothingExtracted")}
                      </p>
                    ) : (
                      <>
                        <ul className="space-y-0.5">
                          {Object.entries(parsed)
                            .filter(([, v]) => v)
                            .map(([k, v]) => (
                              <li key={k} className="text-gray-700 dark:text-gray-200">
                                <span className="text-gray-400">{k}:</span> {v}
                              </li>
                            ))}
                        </ul>
                        <button
                          onClick={() => {
                            const summary = Object.entries(parsed)
                              .filter(([, v]) => v)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join("\n");
                            setForm((f) =>
                              f ? { ...f, notes: f.notes ? `${f.notes}\n${summary}` : summary } : f,
                            );
                          }}
                          className="btn-ghost btn-sm mt-2"
                        >
                          <Plus className="h-4 w-4" />
                          {t("shared.serviceBookings.addToNotes")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {bookings.length === 0 && !form && (
        <p className="text-sm text-gray-400">{t("shared.serviceBookings.empty")}</p>
      )}
    </div>
  );
}
